import { STORAGE_KEYS, API_BASE_URL } from './config'
import { defaultLocale, getLocaleFromPath } from './locales'
import { useAuthStore } from '@/stores/useAuthStore'
import { clearSessionCallerId } from './sessionCallerId'

const TOKEN_REFRESH_INTERVAL = 45 * 60 * 1000

const AUTH_STORAGE_KEYS: readonly string[] = [
  STORAGE_KEYS.ACCESS_TOKEN,
  STORAGE_KEYS.REFRESH_TOKEN,
  STORAGE_KEYS.USER_ID,
  STORAGE_KEYS.USER_EMAIL,
  STORAGE_KEYS.USER_FIRST_NAME,
  STORAGE_KEYS.USER_LAST_NAME,
  STORAGE_KEYS.USER_ROLE,
  STORAGE_KEYS.COMPANY_ID,
  STORAGE_KEYS.COMPANY_NAME,
  'callizy_access_token',
  'callizy_session_caller_id',
]

// Clés localStorage à PRÉSERVER au logout (préférences UI globales, pas
// user-spécifiques). Tout le reste est purgé pour éviter qu'un user voie
// les données du précédent (favoris, caches, state Zustand persist, etc.)
// — bug critique en contexte multi-utilisateurs sur le même device (app Mac).
const LOGOUT_PRESERVED_KEYS: readonly string[] = [
  'callizy_ui',              // useUIStore : darkMode, sidebar, etc.
  'preferred_locale',        // langue préférée
  'callizy_mobile_browser_ack',  // acknowledgement mobile UX
  'callizy_appareil',        // browser secret: "remember this browser" survives a sign-out
]

let twilioTokenRefreshCallback: (() => Promise<void>) | null = null

export function setTwilioTokenRefreshCallback(cb: (() => Promise<void>) | null) {
  twilioTokenRefreshCallback = cb
}

export function clearAllAuthData(): void {
  for (const key of AUTH_STORAGE_KEYS) localStorage.removeItem(key)
  // La ligne choisie vit dans sessionStorage, pas localStorage : sans ceci,
  // elle passait au compte suivant connecté dans le même onglet.
  clearSessionCallerId()
  document.cookie = 'sessionid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
  document.cookie = 'csrftoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
}

/**
 * Purge agressivement TOUT le state local user-spécifique : localStorage
 * (sauf préférences UI globales), sessionStorage, et tous les caches mémoire
 * (TanStack Query, etc.). Appelée au logout pour éviter qu'un autre user
 * qui se logge derrière hérite des données du précédent — bug critique
 * dans le wrapper Mac où WKWebView persiste tout entre les sessions.
 */
export function purgeUserData(): void {
  // 1. localStorage : preserve UI prefs, purge tout le reste
  const preserved: Record<string, string> = {}
  for (const key of LOGOUT_PRESERVED_KEYS) {
    const value = localStorage.getItem(key)
    if (value !== null) preserved[key] = value
  }
  localStorage.clear()
  for (const [key, value] of Object.entries(preserved)) {
    localStorage.setItem(key, value)
  }

  // 2. sessionStorage : tout dégager
  try { sessionStorage.clear() } catch { /* ignoré */ }

  // 3. Cookies auth
  document.cookie = 'sessionid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
  document.cookie = 'csrftoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
}

export function redirectToLogin(reason: 'expired' | 'invalid' | 'none' = 'expired'): void {
  clearAllAuthData()
  if (reason === 'expired') {
    sessionStorage.setItem('login_message', 'Votre session a expiré. Reconnectez-vous pour continuer.')
  } else if (reason === 'invalid') {
    sessionStorage.setItem('login_message', 'Pour votre sécurité, veuillez vous reconnecter.')
  }
  const locale = getLocaleFromPath(window.location.pathname) || defaultLocale
  window.location.href = `/${locale}/login`
}

/** `expire` : la session n'est plus valable, il faut se reconnecter.
 * `indisponible` : le serveur n'a pas pu répondre (panne, 429, réseau) ; la
 * session est peut-être intacte, on ne déconnecte PAS. Le 28 sept 2026, un
 * client actif a été mal aiguillé pendant une rafale de 429. */
export type ResultatJeton = 'ok' | 'expire' | 'indisponible'

export async function refreshJWTToken(): Promise<ResultatJeton> {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  if (!refreshToken) return 'expire'
  try {
    let response: Response
    try {
      response = await fetch(`${API_BASE_URL}/api/token/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      })
    } catch {
      return 'indisponible'
    }

    // 400/401 : jeton refusé par le serveur. Tout le reste (429, 5xx) : le
    // serveur ne sait pas répondre maintenant, ça ne dit rien de la session.
    if (response.status === 400 || response.status === 401) return 'expire'
    if (!response.ok) return 'indisponible'

    const data = await response.json()
    // ⚠️ On doit passer par le setter Zustand pour que l'intercepteur axios et le SSE
    // lisent le NOUVEAU token. Écrire localStorage seul ne réveille pas le store.
    const newRefresh = data.refresh || refreshToken
    useAuthStore.getState().setTokens(data.access, newRefresh)

    window.dispatchEvent(new Event('token-refreshed'))

    if (twilioTokenRefreshCallback) {
      try {
        await twilioTokenRefreshCallback()
      } catch (err) {
        console.error('[Auth] Twilio token refresh callback failed:', err)
      }
    }
    return 'ok'
  } catch (error) {
    console.error('[Auth] Error refreshing token:', error)
    return 'indisponible'
  }
}

export function startTokenRefreshTimer() {
  return setInterval(async () => {
    if ((await refreshJWTToken()) === 'expire') redirectToLogin('expired')
  }, TOKEN_REFRESH_INTERVAL)
}

export async function refreshIfNeeded(): Promise<void> {
  const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
  if (!token) return
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const exp = payload.exp * 1000
    if (exp - Date.now() < 10 * 60 * 1000) await refreshJWTToken()
  } catch (error) {
    console.error('[Auth] Error checking token expiry:', error)
  }
}

export function isTokenFresh(minRemainingMs = 60_000): boolean {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    if (!token) return false
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 - Date.now() > minRemainingMs
  } catch {
    return false
  }
}

export function logout(redirectPath?: string): void {
  void import('posthog-js').then((m) => m.default.reset()).catch(() => {})
  // Invalide d'abord le refresh token côté serveur (fire-and-forget dans clear()).
  // Puis purge agressive localStorage / sessionStorage / cookies.
  useAuthStore.getState().clear()
  purgeUserData()
  // Avertit le shell macOS (s'il est là) pour qu'il puisse purger le cache
  // HTTP WebKit + IndexedDB en plus du localStorage.
  import('./desktopShell').then((m) => m.notifyDesktop('logout')).catch(() => {})
  const locale = getLocaleFromPath(window.location.pathname) || defaultLocale
  window.location.href = redirectPath || `/${locale}/login`
}

export function checkAccountDeleted(): boolean {
  const deleted = localStorage.getItem(STORAGE_KEYS.ACCOUNT_DELETED)
  if (deleted === 'true') {
    localStorage.removeItem(STORAGE_KEYS.ACCOUNT_DELETED)
    return true
  }
  return false
}
